/**
 * Conversation-delete dialog (prompt.md §8.4).
 *
 * WHY this is a shared component and not a per-client prompt: deleting a
 * conversation is a privacy action with a required choice — keep the memories
 * derived from it, or delete them too — and the server refuses a delete that
 * does not state the policy. Web and desktop both mount this dialog so the
 * choice, its wording and its failure path cannot drift between clients.
 *
 * The two options are stated as *outcomes* ("keep memories" / "delete
 * memories"), not as a Yes/No pair, because the safe default is different for
 * each user and an unlabelled confirm button is how the wrong one gets clicked.
 */

import { Button } from "../primitives/Button.tsx";
import { Dialog } from "../primitives/Surfaces.tsx";
import { MUTED_TEXT, rowStyle, stackStyle } from "../styles.ts";

export interface ConversationDeleteDialogProps {
  readonly open: boolean;
  /** Shown so the user can confirm they are deleting the conversation they think. */
  readonly conversationTitle?: string | null;
  /** True while the delete request is in flight; both options are disabled. */
  readonly pending?: boolean;
  /** Server or network error to surface; the dialog stays open. */
  readonly error?: string | null;
  readonly onCancel: () => void;
  readonly onConfirm: (relatedMemories: "keep" | "delete") => void;
}

export function ConversationDeleteDialog({
  open,
  conversationTitle,
  pending = false,
  error = null,
  onCancel,
  onConfirm,
}: ConversationDeleteDialogProps) {
  const title = conversationTitle && conversationTitle.length > 0 ? conversationTitle : "this conversation";
  return (
    <Dialog
      open={open}
      title="Delete conversation"
      description={`“${title}” will be removed. Choose what happens to memories the agent derived from it.`}
      onClose={onCancel}
      footer={
        <Button variant="ghost" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
      }
    >
      <div style={stackStyle("var(--dayvise-space-sm)")}>
        <p style={{ ...MUTED_TEXT, margin: 0 }}>
          Memories the agent wrote from this conversation are private to you. Memories you explicitly promoted to the
          workspace are never deleted either way.
        </p>
        {error === null ? null : (
          <p role="alert" style={{ margin: 0, color: "var(--dayvise-color-danger)" }}>
            {error}
          </p>
        )}
        <div style={rowStyle("var(--dayvise-space-sm)", "flex-end")}>
          <Button variant="secondary" disabled={pending} onClick={() => onConfirm("keep")}>
            Delete, keep memories
          </Button>
          <Button variant="danger" loading={pending} onClick={() => onConfirm("delete")}>
            Delete and remove related memories
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
